import { useEffect, useState } from 'react'
import { CommonUnixMsEnum } from '../../../enums'
import { ColorPalette } from '../../../config'
import { CustomTimePeriod, DateRange, TimePeriod, PeriodTypeEnum, PeriodTypeLabelsEnum } from '../../../types'
import CalendarRange from '../../BaseComponents/Calendar/CalendarRange'
import CheckboxTabLabelled from '../../BaseComponents/Checkboxes/CheckboxTabLabelled'
import { isEmpty } from 'lodash'

const { TODAY, LAST_WEEK, LAST_MONTH, LAST_3_MONTHS, LAST_6_MONTHS, LAST_12_MONTHS, CUSTOM_RANGE } =
  PeriodTypeLabelsEnum
const { DAY, WEEK, MONTH } = PeriodTypeEnum

type DataQueryTimePeriodSelectorProps = {
  timePeriod: TimePeriod | {}
  customTimeRange: CustomTimePeriod | {}
  setTimePeriod: (timePeriod: TimePeriod | undefined) => void
  setCustomDateRange: (timeRange: CustomTimePeriod) => Promise<void>
}

export const DataQueryTimePeriodSelector = (props: DataQueryTimePeriodSelectorProps) => {
  const [selectedOption, setSelectedOption] = useState('')
  const [dateRange, setDateRange] = useState<DateRange | {}>({})
  const [calendarRangeVisible, setCalendarRangeVisibility] = useState(false)
  // const [maxDateRange, setMaxDateRange] = useState(CommonUnixMsEnum.ONE_MONTH * 6)

  useEffect(() => {
    if (props.timePeriod && !isEmpty(props.timePeriod)) {
      const option = getTimePeriodOption()
      setSelectedOption(option)
      return // assuming that only timePeriod or customTimeRange will be set
    }
    if (props.customTimeRange && !isEmpty(props.customTimeRange)) {
      const timeRange = props.customTimeRange as CustomTimePeriod
      setDateRange({
        startDate: new Date(timeRange.startUnixMs),
        endDate: new Date(timeRange.endUnixMs),
      })
      setSelectedOption(CUSTOM_RANGE)
      setCalendarRangeVisibility(true)
    }
  }, [])

  const getTimePeriodOption = () => {
    if (props.timePeriod && !isEmpty(props.timePeriod)) {
      const timePeriod = props.timePeriod as TimePeriod
      switch (timePeriod.periodType) {
        case DAY:
          return TODAY
        case WEEK:
          return LAST_WEEK
        case MONTH:
          switch (timePeriod.periodInteger) {
            case 1:
              return LAST_MONTH
            case 3:
              return LAST_3_MONTHS
            case 6:
              return LAST_6_MONTHS
            case 12:
              return LAST_12_MONTHS
          }
      }
    }
    return ''
  }

  const handleTimePeriodSelection = async (values: string[], isMount: boolean) => {
    if (!values || !values.length) {
      return
    }
    const selectedOption = values[0] as PeriodTypeLabelsEnum
    const selectionMap = {
      [TODAY]: { periodType: DAY, periodInteger: 1 },
      [LAST_WEEK]: { periodType: WEEK, periodInteger: 1 },
      [LAST_MONTH]: { periodType: MONTH, periodInteger: 1 },
      [LAST_3_MONTHS]: { periodType: MONTH, periodInteger: 3 },
      [LAST_6_MONTHS]: { periodType: MONTH, periodInteger: 6 },
      [LAST_12_MONTHS]: { periodType: MONTH, periodInteger: 12 },
      [CUSTOM_RANGE]: undefined,
    }
    if (!selectionMap.hasOwnProperty(selectedOption)) {
      throw new Error('Invalid time period selection')
    }
    const timePeriod = selectionMap[selectedOption] as TimePeriod | undefined
    const isCustomOption = selectedOption === CUSTOM_RANGE
    if (isCustomOption) {
      const now = new Date()
      const dateRange = {
        startDate: new Date(now.getTime() - CommonUnixMsEnum.ONE_MONTH),
        endDate: now,
      }
      setDateRange(dateRange)
      await props.setCustomDateRange({
        startUnixMs: dateRange.startDate.getTime(),
        endUnixMs: dateRange.endDate.getTime(),
      })
    }
    setSelectedOption(selectedOption)
    setCalendarRangeVisibility(isCustomOption)
    props.setTimePeriod(timePeriod)
  }

  const handleCustomDateRangeSelection = (date?: number, rangePoint?: string) => {
    if (!date || !rangePoint) {
      return
    }
    let newDateRange = {
      ...dateRange,
      [rangePoint]: new Date(date),
    }
    setDateRange(newDateRange)
  }

  const highlightCustomRangeSelection = (optionLabel: string) => {
    if (optionLabel === CUSTOM_RANGE && selectedOption === CUSTOM_RANGE) {
      return { fontWeight: 550, letterSpacing: 1.1 }
    }
    return
  }

  const saveOnCustomRangeSelection = async () => {
    const selectedDateRange = dateRange as DateRange
    const unixMs1 = selectedDateRange.startDate.getTime()
    const unixMs2 = selectedDateRange.endDate.getTime()
    const timeRange: CustomTimePeriod = {
      startUnixMs: unixMs1 > unixMs2 ? unixMs2 : unixMs1,
      endUnixMs: unixMs1 > unixMs2 ? unixMs1 : unixMs2,
    }
    await props.setCustomDateRange(timeRange)
    setDateRange(dateRange)
    setCalendarRangeVisibility(true)
  }

  let customDateRangeSelector = null
  if (calendarRangeVisible) {
    customDateRangeSelector = (
      <CalendarRange
        orientation="horizontal"
        dateRange={dateRange}
        onDateChange={handleCustomDateRangeSelection}
        inputStyle={{ border: 'none', width: 225 }}
        dialogStyle={{ zIndex: 30000 }}
        onClose={() => setCalendarRangeVisibility(false)} // CHECK: make sure simply closing the calendar range via 'cancel' doesn't save the selection
        onSave={saveOnCustomRangeSelection}
      />
    )
  }

  return (
    <CheckboxTabLabelled
      style={styles.sectionInput}
      itemLabelStyle={{ color: ColorPalette.PRIMARY_TEXT }}
      label="SELECT A TIME PERIOD"
      values={selectedOption ? [selectedOption] : []}
      selectorItems={[TODAY, LAST_WEEK, LAST_MONTH, LAST_3_MONTHS, LAST_6_MONTHS, CUSTOM_RANGE]}
      selectionHandler={handleTimePeriodSelection}
      selectedLabelStyleAdjustment={highlightCustomRangeSelection}>
      {customDateRangeSelector}
    </CheckboxTabLabelled>
  )
}

const styles = {
  sectionInput: {
    margin: '40px 1.5em 0px 1.5em',
  },
}
